import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, CardContent } from "../../ui/card";
import StatusBadge from "./StatusBadge";
import SkeletonCard from "./SkeletonCard";
import { Music, User, ArrowLeft } from "lucide-react";

const AllSongs = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecentEdits = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/song-edits/recent`, {
          params: { limit: 100 },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        });

        const formattedSongs = await Promise.all(
          response.data.map(async (edit) => {
            let title = edit.songTitle || t("recentlyEditedSongs.untitledSong");

            // Only fetch song title if songTitle is missing or invalid and songId is valid
            if ((!edit.songTitle || edit.songTitle === t("recentlyEditedSongs.untitledSong")) && edit.songId) {
              try {
                const songIdNum = parseInt(edit.songId, 10);
                if (!isNaN(songIdNum)) {
                  const songResponse = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${songIdNum}`,
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
                      },
                    }
                  );
                  console.log(`Fetched title for songId ${songIdNum}:`, songResponse.data.title);
                  title = songResponse.data.title || t("recentlyEditedSongs.untitledSong");
                } else {
                  console.warn(`Invalid songId: ${edit.songId}`);
                }
              } catch (songError) {
                console.error(`Error fetching song title for songId ${edit.songId}:`, songError.response?.data || songError.message);
                title = t("recentlyEditedSongs.untitledSong");
              }
            }

            return {
              ...edit,
              songTitle: title,
            };
          })
        );

        setSongs(formattedSongs);
      } catch (err) {
        console.error("Error fetching recent edits:", err.response?.data || err.message);
        setError(t("recentlyEditedSongs.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchRecentEdits();
  }, [t, i18n.language]);

  const formatRole = (role) =>
    t(`recentlyEditedSongs.roles.${role.toLowerCase()}`, role.charAt(0).toUpperCase() + role.slice(1));

  return (
    <div className="max-w-4xl mx-auto py-6 px-2">
      {/* Back button with translation */}
      <button
        className="mb-4 flex items-center gap-2 text-orange-500 hover:text-orange-700 text-sm font-medium"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4" /> {t("recentlyEditedSongs.back")}
      </button>
      {/* Page title */}
      <h1 className="text-2xl font-bold mb-4 text-neutral-800">{t("recentlyEditedSongs.title")}</h1>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-2xl border border-gray-100 shadow">
          <CardContent className="p-4 text-red-500 text-center text-sm">{error}</CardContent>
        </Card>
      ) : songs.length === 0 ? (
        <Card className="rounded-2xl border border-gray-100 shadow">
          <CardContent className="py-8 text-center text-neutral-700 text-sm">
            {t("recentlyEditedSongs.noSongs")}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {songs.map((song) => (
            <Card
              key={song._id || song.id}
              className="rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow group cursor-pointer"
              onClick={() => song.songId && navigate(`/edit-song/${song.songId}`)}
            >
              <CardContent className="p-4 flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-1">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <Music className="w-6 h-6 text-zinc-400" />
                  </div>
                  <div>
                    {/* Song title with fallback */}
                    <div className="font-medium text-neutral-800 text-base truncate">
                      {song.songTitle || t("recentlyEditedSongs.untitledSong")}
                    </div>
                    {/* Username with fallback */}
                    <div className="text-xs text-zinc-400 flex items-center gap-1">
                      <User className="w-4 h-4 text-zinc-400" />
                      {song.userId?.username || song.user || t("recentlyEditedSongs.unknownUser")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {/* Role badge */}
                  <StatusBadge status={song.role || song.userId?.role || "super user"}>
                    {formatRole(song.role || song.userId?.role || "super user")}
                  </StatusBadge>
                  {/* Edited date with fallback */}
                  <span className="text-xs text-zinc-400 ml-2">
                    {song.editedAt
                      ? new Date(song.editedAt).toLocaleDateString(i18n.language, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : t("recentlyEditedSongs.recently")}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllSongs;